import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const CartContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('cart');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const syncTimer = useRef(null);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
    // Debounce cart sync to backend (for abandoned cart recovery)
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => {
      syncCartToBackend(cartItems);
    }, 2000);
  }, [cartItems]);

  const syncCartToBackend = async (items) => {
    try {
      await fetch(`${API}/cart/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ items }),
      });
    } catch { /* Not logged in or network error — ignore */ }
  };

  const addToCart = (artwork) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.id === artwork.id);
      if (existing) {
        return prev.map(item =>
          item.id === artwork.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...artwork, quantity: 1 }];
    });
  };

  const removeFromCart = (artworkId) => {
    setCartItems(prev => prev.filter(item => item.id !== artworkId));
  };

  const updateQuantity = (artworkId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(artworkId);
      return;
    }
    setCartItems(prev =>
      prev.map(item =>
        item.id === artworkId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount
      }}
    >
      {children}
    </CartContext.Provider>
  );
};